import { BUS_ACCESS_TOKEN, BUS_GLOBAL_LANGUAGE, BUS_USER_INFO, type ISgConnectUserInfo } from './auth/bus-topics';
import { type WidgetEnvironmentExtended, getSgwtConnectLibrary } from './sgwt-widgets-utils';
const packageVersion = require('../../package.json').version;

export enum SgwtWidgetName {
  ACCOUNT_CENTER = 'sgwt-account-center',
  CONNECT = 'sgwt-connect',
  HELP_CENTER = 'sgwt-help-center',
  MINI_FOOTER = 'sgwt-mini-footer',
  SPLASH_SCREEN = 'sgwt-splash-screen',
  WEB_ANALYTICS = 'sgwt-web-analytics',
  SGWT_CHATBOT = 'sgwt-chatbot',
}

type EventDetails = Record<string, string | number | boolean | null | undefined>;

interface IMonitoringEvent {
  applicationId?: string;
  cdn: string | null;
  date: string;
  domain: string;
  event: string;
  details?: EventDetails;
  language?: string;
  sgbs: string;
  url: string;
  userAgent: string;
  version: string;
  widget: SgwtWidgetName;
}

declare global {
  interface Window {
    __sgwtWidgetsEventsQueue?: {
      events: IMonitoringEvent[];
      timeoutId: number | null;
    };
  }
}

const API_ENDPOINTS: Record<WidgetEnvironmentExtended, string> = {
  development: 'https://sgwt-api-sgwt-monitoring-uat.sgmarkets.world.socgen/api/v1/monitoring/sgwt-widgets',
  internal_homologation: 'https://sgwt-api-sgwt-monitoring-uat.sgmarkets.world.socgen/api/v1/monitoring/sgwt-widgets',
  homologation: 'https://api-s-uat.sgmarkets.com/widget/webtoolkit/sgwt-monitoring/v1/monitoring/sgwt-widgets',
  internal_production: 'https://sgwt-api-sgwt-monitoring.sgmarkets.world.socgen/api/v1/monitoring/sgwt-widgets',
  production: 'https://api-s.sgmarkets.com/widget/webtoolkit/sgwt-monitoring/v1/monitoring/sgwt-widgets',
};

const HOMOLOGATION_API = 'https://api-s-uat.sgmarkets.com/widget/webtoolkit/sgwt-monitoring/v1/monitoring/sgwt-widgets';

// Time in ms waited before sending the event(s) to the API...
const WAITING_TIME_BEFORE_SENDING_EVENTS = 5000;

function getFromWidgetBus<T = string>(topic: string): T | undefined {
  return window.SGWTWidgetConfiguration?.bus?.dangerouslyGetCurrentValue<T>(topic);
}

/**
 * Returns an object without the key that have a `undefined`, `null` or `""` value. **Works only on one level**.
 * If the object is empty, it returns `undefined`.
 */
function cleanObject(obj?: EventDetails | null): EventDetails | undefined {
  if (!obj) {
    return undefined;
  }
  const data = Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== ''));
  return Object.keys(data).length > 0 ? data : undefined;
}

function getAPIEndpoint(): string {
  const userInfo = getFromWidgetBus<ISgConnectUserInfo>(BUS_USER_INFO);
  const isOnLAN = userInfo?.origin_network === 'LAN';

  const env: string | null | undefined = window.SGWTWidgetConfiguration ? window.SGWTWidgetConfiguration.environment : null;
  const isProd = !env || ['prd', 'prod', 'production'].includes(env.toLowerCase());

  if (isProd) {
    return isOnLAN ? API_ENDPOINTS.internal_production : API_ENDPOINTS.production;
  }
  return isOnLAN ? API_ENDPOINTS.internal_homologation : API_ENDPOINTS.homologation;
}

class SgwtWidgetsMonitoring {
  private apiEndpoint: string;
  private applicationId: string | undefined;
  private cdnUrls: Record<string, string | null>;

  constructor() {
    if (!window.__sgwtWidgetsEventsQueue) {
      this.initializeQueue();
    }
    this.apiEndpoint = getAPIEndpoint();
    this.cdnUrls = {};
  }

  private initializeQueue() {
    window.__sgwtWidgetsEventsQueue = {
      events: [],
      timeoutId: null,
    };
  }

  private getToken(): string | undefined | null {
    // Get the sgwtConnect from the widget or from the window object...
    const sgwtConnect: any = getSgwtConnectLibrary();
    // Get the authorization token...
    if (sgwtConnect) {
      return sgwtConnect.getAuthorizationHeader();
    }
    const fromBus = getFromWidgetBus(BUS_ACCESS_TOKEN);
    return fromBus || null;
  }

  private sendEvents() {
    if (!window.__sgwtWidgetsEventsQueue) {
      return;
    }
    if (window.__sgwtWidgetsEventsQueue.events.length > 0) {
      const events = [...window.__sgwtWidgetsEventsQueue.events];
      window.__sgwtWidgetsEventsQueue.events = [];
      const token = this.getToken();
      if (token) {
        const options: any = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify(events),
        };
        fetch(this.apiEndpoint, options).catch((err) => console.log('monitoring not sent due to error', err));
      } else {
        console.debug('monitoring not sent due to missing token');
      }
    }
    window.__sgwtWidgetsEventsQueue.timeoutId = null;
  }

  private getCdnPath(widget: string): string | null {
    let path = this.cdnUrls[widget];
    if (path !== undefined) {
      return path;
    }
    const scriptTag = document.querySelector<HTMLScriptElement>(`script[src*="${widget}.js"]`);
    if (scriptTag) {
      path = scriptTag.src;
    } else {
      path = null;
    }
    this.cdnUrls[widget] = path;
    return path;
  }

  private currentLocation() {
    const url = window.location.toString();
    const domain = url
      .substring(0, url.indexOf('/', 8)) // remove the sub-routes to keep the domain
      .replace(/^https?:\/\//, '') // remove the protocol part
      .replace(/:\d+/, ''); // remove the port
    return { url, domain };
  }

  public queueEvent(widget: SgwtWidgetName, eventName: string, eventDetails?: EventDetails | null) {
    if (!window.__sgwtWidgetsEventsQueue) {
      this.initializeQueue();
    }

    const language = getFromWidgetBus(BUS_GLOBAL_LANGUAGE);
    const event: IMonitoringEvent = {
      applicationId: this.applicationId,
      date: new Date().toISOString(),
      event: eventName,
      cdn: this.getCdnPath(widget),
      details: cleanObject(eventDetails) || undefined,
      language,
      userAgent: window.navigator.userAgent,
      sgbs: 'v4',
      version: packageVersion,
      widget,
      ...this.currentLocation(),
    };

    window.__sgwtWidgetsEventsQueue!.events.push(event);
    if (window.__sgwtWidgetsEventsQueue!.timeoutId) {
      // Some events are already in the queue, so we reset the timeout...
      window.clearTimeout(window.__sgwtWidgetsEventsQueue!.timeoutId as number);
    }
    window.__sgwtWidgetsEventsQueue!.timeoutId = window.setTimeout(() => this.sendEvents(), WAITING_TIME_BEFORE_SENDING_EVENTS);
  }

  public widgetLoaded(widget: SgwtWidgetName, applicationId?: string | null, configuration?: EventDetails | null) {
    this.applicationId = applicationId || undefined;
    this.queueEvent(widget, 'loaded', configuration);
  }
}

const monitoring = new SgwtWidgetsMonitoring();

export function startWidgetMonitoring(widget: SgwtWidgetName, applicationId?: string | null, configuration?: EventDetails | null) {
  monitoring.widgetLoaded(widget, applicationId, configuration);
}

export function registerEvent(widget: SgwtWidgetName, eventName: string, eventDetails?: EventDetails | null) {
  monitoring.queueEvent(widget, eventName, eventDetails);
}

export function registerAccountCenterEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.ACCOUNT_CENTER, eventName, eventDetails);
}

export function registerMiniFooterEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.MINI_FOOTER, eventName, eventDetails);
}

export function registerHelpCenterEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.HELP_CENTER, eventName, eventDetails);
}

export function registerSplashScreenEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.SPLASH_SCREEN, eventName, eventDetails);
}

export function registerMainIssue(widget: SgwtWidgetName, error: Error) {
  registerEvent(widget, 'general-issue', { message: error.stack });
}
